// material-ui
import { styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMarkAsReadMutation } from '../../../../services/notification/notification';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { openMoveResultModal } from 'redux/slices/moveResultModalSlice';
import { useLazyGetCustomDayByIdQuery } from 'services/content/custom-days';

dayjs.extend(relativeTime);

// Styled wrapper with dynamic read/unread background
const ListItemWrapper = styled(Box)(({ theme, isread }) => ({
  cursor: 'pointer',
  padding: 10,
  backgroundColor: isread === 'false' ? '#999999' : '#544e63', // Unread = Gold, Read = Dark Purple
  color: 'white',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#673ab7',
    color: 'white'
  },
  '& .MuiListItem-root': {
    padding: 0
  },
  '& .MuiTypography-root': {
    color: 'white'
  }
}));

const NotificationList = ({ data = [], loading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [triggerGetDay] = useLazyGetCustomDayByIdQuery();
  const [markAsRead] = useMarkAsReadMutation();

  const handleMarkAsRead = async (item) => {
    if (!item?.isRead) {
      try {
        await markAsRead({ id: item?._id }).unwrap();
        // toast.success('Notification marked as read.');
      } catch (error) {
        const messages = Array.isArray(error?.data?.message) ? error.data.message : [error?.data?.message];
        messages.forEach((msg) => toast.error(msg));
      }
    }
  };

  return (
    <List
      sx={{
        width: '100%',
        maxWidth: 330,
        py: 0,
        borderRadius: '10px',
        '& .MuiListItemSecondaryAction-root': {
          top: 22
        },
        '& .MuiDivider-root': {
          my: 0
        },
        '& .list-container': {
          pl: 7
        }
      }}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
          <CircularProgress />
        </Box>
      ) : (
        data?.map((item, index) => (
          <Box key={item._id || index}>
            <ListItemWrapper
              onClick={() => {
                handleMarkAsRead(item);
                if (item?.type === 'moveResult' && item?.moveResultId) {
                  dispatch(openMoveResultModal(item?.moveResultId));
                  return;
                }
                if (item?.type === 'customWorkoutComplete' && item?.dayId) {
                  triggerGetDay({ id: item.dayId })
                    .unwrap()
                    .then((day) => {
                      const clientId = day?.user?._id || item?.client?._id || item?.client || '';
                      const params = new URLSearchParams({
                        client: clientId,
                        programId: day?.program?._id || '',
                        weekId: day?.week?._id || '',
                        trainingRequestId: day?.trainingRequestId || '',
                        name: day?.user?.fullName || day?.user?.userName || '',
                        'program-name': day?.program?.title || '',
                        'week-name': day?.week?.title || '',
                        'client-status': 'active'
                      });
                      navigate(`/custom-days?${params.toString()}`);
                    })
                    .catch(() => {
                      const clientId = item?.client?._id || item?.client || '';
                      navigate(`/custom-days?client=${clientId}`);
                    });
                  return;
                }
                if (item?.requestId && item?.title === 'Assessment completed') {
                  navigate(`/custom-moves?requestId=${item?.requestId}&client=${item?.client?._id}&name=${item?.client?.fullName}`);
                }
                if (item?.requestId && item?.title !== 'Assessment completed') {
                  navigate(`/training-requests?request-id=${item?.requestId}`);
                }
              }}
              isread={String(item?.isRead)}
            >
              <ListItem alignItems="center">
                <ListItemAvatar>
                  {item?.sender?.profileImage ? (
                    <Avatar alt="user" src={item.sender.profileImage} />
                  ) : (
                    <Avatar sx={{ bgcolor: '#673ab7' }}>
                      <NotificationsIcon sx={{ color: 'white' }} />
                    </Avatar>
                  )}
                </ListItemAvatar>

                <ListItemText primary={<Typography variant="subtitle1">{item?.sender?.fullName || 'System Notification'}</Typography>} />

                <ListItemSecondaryAction>
                  <Typography variant="caption" sx={{ color: 'inherit' }}>
                    {dayjs(item?.createdAt).fromNow()}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>

              <Grid container direction="column" className="list-container">
                <Grid item xs={12} sx={{ pb: 1 }}>
                  <Typography variant="body2">{item?.body}</Typography>
                </Grid>
              </Grid>
            </ListItemWrapper>
            <Divider />
          </Box>
        ))
      )}
    </List>
  );
};

export default NotificationList;
